import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, fetchData } from '../services/api';
import { PATH_SEGMENTS, route } from '../config/paths';
import PlaybackPanel from '../components/PlaybackPanel';
import TranscriptPanel from '../components/TranscriptPanel';
import AgentAssistCard from '../components/AgentAssistCard';
import AgentAssistLiveCard from '../components/AgentAssistLiveCard';
import LiveAssistCard from '../components/LiveAssistCard';
import {
  InteractionResponse,
  TranscriptResponse,
  TurnAnalysisResponse,
  TurnAnalysisTurn,
} from '../types/api';
import '../styles/screens/LiveAssist.css';

const AGENT_ASSIST_UI_STORAGE_KEY = 'liveAssist.agentAssistUiMode';
type AgentAssistUiMode = 'compact' | 'focus' | 'focus_compact' | 'timeline';

/**
 * Recorded interaction replay: playback + transcript slice + agent assist (compact, focus, or timeline) from stored turn analysis.
 */
const LiveAssistReplay: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [interactionData, setInteractionData] = useState<InteractionResponse | null>(null);
  const [transcriptData, setTranscriptData] = useState<TranscriptResponse | null>(null);
  const [currentTimePosition, setCurrentTimePosition] = useState(0);
  const [activeSegmentId, setActiveSegmentId] = useState<string | null>(null);
  const [allTurns, setAllTurns] = useState<TurnAnalysisTurn[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [audioUnavailable, setAudioUnavailable] = useState(false);
  const [agentAssistUiMode, setAgentAssistUiMode] = useState<AgentAssistUiMode>(() => {
    if (typeof window === 'undefined') return 'focus';
    try {
      const v = window.localStorage.getItem(AGENT_ASSIST_UI_STORAGE_KEY);
      if (v === 'compact' || v === 'focus' || v === 'focus_compact' || v === 'timeline') return v;
    } catch {
      /* ignore */
    }
    return 'focus';
  });
  const audioRef = useRef<HTMLAudioElement>(null);

  const transcriptId = interactionData?.transcript_id ?? transcriptData?.transcript?.id;
  const speechCount = transcriptData?.transcript.speech_texts.length ?? 0;

  const transcriptTimestamps = useMemo(() => {
    return transcriptData?.transcript.speech_texts.map((speech) => ({
      id: (speech.id || `speech-${speech.start_ms}`).toString(),
      startTime: speech.start_ms,
      endTime: speech.end_ms || speech.start_ms + 10000,
      speaker: { id: speech.speaker_id, name: speech.speaker_name },
    })) ?? [];
  }, [transcriptData]);

  const moments = useMemo(() => {
    if (!transcriptData) return [];
    const all: { id: string; name: string; time: number }[] = [];
    transcriptData.transcript.speech_texts.forEach((speech) => {
      speech.transcript_mmts?.forEach((moment) => {
        all.push({
          id: `moment-${moment.id}`,
          name: moment.name,
          time: speech.start_ms,
        });
      });
    });
    return all;
  }, [transcriptData]);

  const totalDuration = useMemo(() => {
    if (!transcriptData || !interactionData) return 0;
    const durationSec = interactionData.interaction.duration ?? 0;
    const maxEndMs = Math.max(0, ...transcriptData.transcript.speech_texts.map((s) => s.end_ms || 0));
    return durationSec > 0 ? durationSec * 1000 : maxEndMs;
  }, [interactionData, transcriptData]);

  const currentSegmentIndex = useMemo(() => {
    if (transcriptTimestamps.length === 0) return -1;
    const timeMs = currentTimePosition * 1000;
    const n = transcriptTimestamps.length;
    // Half-open intervals [start, end) for all but the last row so a shared boundary
    // (next start_ms === previous end_ms) maps to the *next* utterance — matches click-to-seek
    // and avoids showing the prior turn's assist on the following line.
    const idx = transcriptTimestamps.findIndex((s, i) => {
      const isLast = i === n - 1;
      if (isLast) return timeMs >= s.startTime && timeMs <= s.endTime;
      return timeMs >= s.startTime && timeMs < s.endTime;
    });
    if (idx >= 0) return idx;
    if (timeMs < transcriptTimestamps[0].startTime) return 0;
    let lastStarted = -1;
    for (let i = transcriptTimestamps.length - 1; i >= 0; i--) {
      if (transcriptTimestamps[i].startTime <= timeMs) {
        lastStarted = i;
        break;
      }
    }
    return lastStarted >= 0 ? lastStarted : 0;
  }, [currentTimePosition, transcriptTimestamps]);

  /**
   * Always request the full TA slice for this transcript (`to_turn` is list end index; see
   * TurnAnalysisQuery._slice_turns). Partial fetches shrink `allTurns` when seeking backward and
   * break focus/timeline cards; payload size is usually small relative to one LLM call.
   */
  const taFetchToTurn = useMemo(() => Math.max(1, speechCount), [speechCount]);

  useEffect(() => {
    if (currentSegmentIndex >= 0 && transcriptTimestamps[currentSegmentIndex]) {
      setActiveSegmentId(transcriptTimestamps[currentSegmentIndex].id);
    }
  }, [currentSegmentIndex, transcriptTimestamps]);

  useEffect(() => {
    if (!activeSegmentId) return;
    const timer = requestAnimationFrame(() => {
      const el = document.getElementById(activeSegmentId);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => cancelAnimationFrame(timer);
  }, [activeSegmentId]);

  useEffect(() => {
    setAudioUnavailable(false);
  }, [id]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(AGENT_ASSIST_UI_STORAGE_KEY, agentAssistUiMode);
    } catch {
      /* ignore */
    }
  }, [agentAssistUiMode]);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setError(null);
      try {
        const interaction = await fetchData<InteractionResponse>(api(PATH_SEGMENTS.INTERACTION, id));
        setInteractionData(interaction);
        const tid = interaction?.transcript_id;
        if (tid) {
          const transcript = await fetchData<TranscriptResponse>(
            api(PATH_SEGMENTS.TRANSCRIPT, tid.toString())
          );
          setTranscriptData(transcript);
        } else {
          setError('No transcript for this interaction.');
        }
      } catch (e) {
        console.error('LiveAssist replay load error', e);
        setError('Failed to load interaction or transcript.');
      }
    };
    load();
  }, [id]);

  const fetchAllTurns = useCallback(async (toTurn: number) => {
    if (!transcriptId || toTurn < 1) return;
    try {
      const url = `${api(PATH_SEGMENTS.TA, transcriptId)}?include=strict,freeflow&from_turn=1&to_turn=${toTurn}`;
      const response = await fetchData<TurnAnalysisResponse>(url);
      setAllTurns(response.turns ?? []);
    } catch (e) {
      console.error('Failed to load turn history', e);
      setAllTurns([]);
    }
  }, [transcriptId]);

  useEffect(() => {
    if (!transcriptId || taFetchToTurn < 1) return;
    void fetchAllTurns(taFetchToTurn);
  }, [transcriptId, taFetchToTurn, fetchAllTurns]);

  const handleTimeUpdate = useCallback((timeMs: number) => {
    setCurrentTimePosition(timeMs / 1000);
  }, []);

  const handlePlayingSegmentChange = useCallback((segmentId: string) => {
    setActiveSegmentId(segmentId);
  }, []);

  const highlightText = useCallback((text: string, _speechId: string) => text, []);

  const handleTranscriptClick = useCallback(
    (speechId: string) => {
      const speeches = transcriptData?.transcript.speech_texts;
      if (!speeches?.length) return;
      const idx = speeches.findIndex(
        (s) =>
          String(s.id ?? '') === speechId ||
          (s.id == null && `speech-${s.start_ms}` === speechId)
      );
      if (idx < 0) return;
      const startMs = speeches[idx].start_ms;
      setCurrentTimePosition(startMs / 1000);
      if (audioRef.current && !audioUnavailable) {
        audioRef.current.currentTime = startMs / 1000;
        void audioRef.current.play();
      }
    },
    [transcriptData, audioUnavailable]
  );

  /**
   * View index = transcript row the user is on (playhead / clicked line). Persisted TaTurn for
   * `speech_text_id` N holds the FSM snapshot *after* utterance N is processed (guidance for what
   * comes next). While viewing row k (0-based), show assist through row k-1 only: slice [0, k).
   * Row 0 alone → no assist yet.
   */
  const viewSpeechIndex = currentSegmentIndex;

  const assistSpeechTextIds = useMemo(() => {
    if (!transcriptData || viewSpeechIndex < 1) return new Set<number>();
    return new Set(
      transcriptData.transcript.speech_texts
        .slice(0, viewSpeechIndex)
        .map((s) => s.id)
        .filter((id): id is number => typeof id === 'number' && id > 0)
    );
  }, [transcriptData, viewSpeechIndex]);

  const turnsThroughPriorUtterance = useMemo(
    () => allTurns.filter((t) => assistSpeechTextIds.has(t.speech_text_id)),
    [allTurns, assistSpeechTextIds]
  );

  const timelineTurnNumber = useMemo(() => {
    if (turnsThroughPriorUtterance.length === 0) return 0;
    return Math.max(...turnsThroughPriorUtterance.map((t) => t.turn_number));
  }, [turnsThroughPriorUtterance]);

  const turnsForTimeline = useMemo(
    () => (timelineTurnNumber >= 1 ? allTurns.filter((t) => t.turn_number <= timelineTurnNumber) : []),
    [allTurns, timelineTurnNumber]
  );

  const assistSpeechIdForCompact = useMemo(() => {
    if (!transcriptData || viewSpeechIndex < 1) return null;
    const st = transcriptData.transcript.speech_texts[viewSpeechIndex - 1];
    const sid = st?.id;
    return typeof sid === 'number' && sid > 0 ? sid : null;
  }, [transcriptData, viewSpeechIndex]);

  const currentTurnForAssist = useMemo(() => {
    if (assistSpeechIdForCompact == null) return null;
    return allTurns.find((t) => t.speech_text_id === assistSpeechIdForCompact) ?? null;
  }, [allTurns, assistSpeechIdForCompact]);

  const transcriptDataUpToCurrent = useMemo(() => {
    if (!transcriptData) return null;
    if (audioUnavailable) {
      return { transcript: transcriptData.transcript };
    }
    const end = currentSegmentIndex >= 0 ? currentSegmentIndex + 1 : 0;
    const speech_texts = transcriptData.transcript.speech_texts.slice(0, end);
    return {
      transcript: {
        ...transcriptData.transcript,
        speech_texts,
      },
    };
  }, [transcriptData, currentSegmentIndex, audioUnavailable]);

  if (!id) {
    return (
      <div className="live-assist-select">
        <p>Open replay for a specific interaction, or start a real-time Live Assist session.</p>
        <p>
          <Link to={route(PATH_SEGMENTS.LIVE_ASSIST, PATH_SEGMENTS.LIVE_ASSIST_LIVE)}>Real-time session</Link>
          {' · '}
          <Link to={route(PATH_SEGMENTS.INTERACTIONS)}>Interactions</Link>
          {' · '}
          <Link to={route(PATH_SEGMENTS.LIVE_ASSIST)}>Live Assist home</Link>
        </p>
      </div>
    );
  }

  if (error) {
    return <div className="live-assist-error">{error}</div>;
  }

  if (!interactionData || !transcriptData) {
    return <div className="live-assist-loading">Loading…</div>;
  }

  return (
    <div className="live-assist">
      <div className="live-assist-toolbar">
        <span className="live-assist-toolbar-label">Replay</span>
        <Link
          to={`${route(PATH_SEGMENTS.LIVE_ASSIST, PATH_SEGMENTS.LIVE_ASSIST_LIVE)}?resume=${encodeURIComponent(id)}`}
          className="live-assist-toolbar-link"
        >
          Switch to real-time
        </Link>
      </div>
      {audioUnavailable && (
        <div
          style={{
            padding: '8px 12px',
            margin: '0 0 8px',
            background: '#fff7ed',
            border: '1px solid #fed7aa',
            borderRadius: 6,
            fontSize: '0.9rem',
            color: '#9a3412',
          }}
        >
          Audio file is not available for this interaction. Transcript and turn analysis still load below.
        </div>
      )}
      <div className="live-assist-playback">
        <PlaybackPanel
          audioRef={audioRef}
          audioSrc={api(PATH_SEGMENTS.AUDIO, id)}
          currentTranscriptId={activeSegmentId ?? undefined}
          onTimeUpdate={handleTimeUpdate}
          onPlayingSegmentChange={handlePlayingSegmentChange}
          transcriptTimestamps={transcriptTimestamps}
          moments={moments}
          totalDuration={totalDuration}
          currentTimePosition={currentTimePosition}
          setCurrentTimePosition={setCurrentTimePosition}
          onMediaError={() => setAudioUnavailable(true)}
        />
      </div>
      <div className="live-assist-columns">
        <div className="live-assist-transcript">
          {transcriptDataUpToCurrent && (
            <TranscriptPanel
              transcriptData={transcriptDataUpToCurrent}
              activeSegmentId={activeSegmentId}
              highlightText={highlightText}
              onTranscriptClick={handleTranscriptClick}
              onTranscriptMouseEnter={() => {}}
            />
          )}
        </div>
        <div className="live-assist-right">
          <div className="live-assist-live-card">
            <div
              className="live-assist-agent-assist-toggle"
              role="group"
              aria-label="Agent assist layout"
            >
              <span className="live-assist-agent-assist-toggle-label">Assist view</span>
              <button
                type="button"
                className={agentAssistUiMode === 'compact' ? 'is-active' : ''}
                onClick={() => setAgentAssistUiMode('compact')}
              >
                Compact card
              </button>
              <button
                type="button"
                className={agentAssistUiMode === 'focus' ? 'is-active' : ''}
                onClick={() => setAgentAssistUiMode('focus')}
              >
                Focus live
              </button>
              <button
                type="button"
                className={agentAssistUiMode === 'focus_compact' ? 'is-active' : ''}
                onClick={() => setAgentAssistUiMode('focus_compact')}
              >
                Focus live compact
              </button>
              <button
                type="button"
                className={agentAssistUiMode === 'timeline' ? 'is-active' : ''}
                onClick={() => setAgentAssistUiMode('timeline')}
              >
                Timeline
              </button>
            </div>
            <div className="live-assist-live-card-body">
              {agentAssistUiMode === 'compact' ? (
                currentTurnForAssist ? (
                  <AgentAssistCard card={currentTurnForAssist.agent_card} />
                ) : (
                  <div className="live-assist-live-card-empty">No turn analysis for this position.</div>
                )
              ) : agentAssistUiMode === 'focus' || agentAssistUiMode === 'focus_compact' ? (
                <AgentAssistLiveCard
                  allTurns={turnsForTimeline}
                  currentTurnNumber={timelineTurnNumber}
                  heroVariant={agentAssistUiMode === 'focus_compact' ? 'compact' : 'default'}
                />
              ) : (
                <LiveAssistCard allTurns={turnsForTimeline} currentTurnNumber={timelineTurnNumber} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveAssistReplay;
